import { defineStore } from "pinia";
import fightsApi, { FightsQueuesResult, TimeCalculatingFightsCollection } from "../Api/fights";
import { ColorSchema, Mat } from "../Common/types";
import { FightQueueStatus } from "../Common/Fights/types";
import { WebsocketClientType } from "../Common/websocketClient";
import type { LiveFightRoundStatus } from "../Public/liveFightScores";

export const usePublicFightsQueueStore = defineStore("public-fights-queue", {
    state: () => {
        return {
            websocketClient: null as WebsocketClientType | null,

            //Event data
            eventId: 0,
            eventTimezone: null as string | null,
            eventName: "" as string,

            //Displaying fights lists
            fightsForMat: {} as Record<number, TimeCalculatingFightsCollection>,
            fightQueueStatuses: {} as Record<number, FightQueueStatus>,
            matsForGivenPage: [] as Mat[],

            //Tracking times for each mat: fightId => left sec.
            trackedTimeForFights: {} as Record<number, number>,
            trackedRoundStatusesForFights: {} as Record<number, LiveFightRoundStatus>,

            //Options (from query params)
            matsPerPage: 6,
            selectedMatIds: [] as number[],
            fightsLimit: 15,
            pageNumber: 0,
            colorSchema: "default" as ColorSchema,

            //Timer
            timer: 20,
            isTimerRunning: false,
            isLoading: false,
        };
    },
    getters: {
        haveFightsForCurrentPage(state): boolean {
            for (const mat of state.matsForGivenPage) {
                const fights = state.fightsForMat[mat.id];
                if (fights && fights.length > 0) {
                    return true;
                }
            }
            return false;
        },
        matNameById: state => {
            return (matId: number): string => {
                const foundMat = state.matsForGivenPage.find(mat => mat.id === matId);
                return foundMat ? foundMat.name : "";
            };
        },
    },
    actions: {
        /**
         * Load state from URL query parameters
         */
        loadFromQueryParams(params: URLSearchParams): void {
            const matIds = params.get("matIds");
            if (matIds) {
                this.selectedMatIds = matIds
                    .split(",")
                    .map(id => parseInt(id, 10))
                    .filter(id => !isNaN(id));
            }

            const pageSize = params.get("pageSize");
            if (pageSize) {
                const parsed = parseInt(pageSize, 10);
                if (!isNaN(parsed) && parsed > 0) {
                    this.matsPerPage = parsed;
                }
            }

            const limit = params.get("limit");
            if (limit) {
                const parsed = parseInt(limit, 10);
                if (!isNaN(parsed) && parsed > 0) {
                    this.fightsLimit = parsed;
                }
            }

            const colorSchema = params.get("colorSchema");
            if (colorSchema && (colorSchema === "default" || colorSchema === "redblue")) {
                this.colorSchema = colorSchema;
            }

            const page = params.get("page");
            if (page) {
                const parsed = parseInt(page, 10);
                if (!isNaN(parsed) && parsed >= 0) {
                    this.pageNumber = parsed;
                }
            }
        },

        /**
         * Refresh fights data from API
         */
        refreshAllMats(): Promise<void> {
            this.isLoading = true;
            return fightsApi
                .getPublicFightsQueues(this.eventId, this.selectedMatIds, this.fightsLimit, this.matsPerPage, this.pageNumber)
                .then((response: FightsQueuesResult) => {
                    // If no mats returned and we're not on page 0, reset to page 0
                    if (response.matsForGivenPage.length === 0 && this.pageNumber > 0) {
                        this.pageNumber = 0;
                        return this.refreshAllMats();
                    }

                    this.fightsForMat = response.fightsCollections;
                    this.fightQueueStatuses = response.fightQueueStatuses;
                    this.matsForGivenPage = response.matsForGivenPage;
                    return Promise.resolve();
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        /**
         * Start the auto-refresh timer
         */
        startTimer(): void {
            if (this.isTimerRunning) {
                return;
            }

            this.isTimerRunning = true;
            this.timer = 20;
        },

        /**
         * Stop the auto-refresh timer
         */
        stopTimer(): void {
            this.isTimerRunning = false;
        },

        /**
         * Tick the timer down by one second
         */
        tickTimer(): void {
            if (!this.isTimerRunning) {
                return;
            }

            this.timer--;

            if (this.timer <= 0) {
                this.onTimerExpired();
            }
        },

        /**
         * Handle timer expiration - cycle to next page and refresh
         */
        onTimerExpired(): void {
            this.pageNumber++;
            this.timer = 20;

            this.refreshAllMats().catch(error => {
                console.error("Failed to refresh fights:", error);
            });
        },

        /**
         * Update tracked time for a specific fight (from WebSocket)
         */
        updateTrackedTime(fightId: number, seconds: number): void {
            this.trackedTimeForFights[fightId] = seconds;
        },

        updateTrackedRoundStatus(fightId: number, roundStatus: LiveFightRoundStatus): void {
            this.trackedRoundStatusesForFights[fightId] = roundStatus;
        },

        removeTrackedRoundStatus(fightId: number): void {
            delete this.trackedRoundStatusesForFights[fightId];
        },
    },
});
